import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  FloppyDisk, Plus, Trash, ArrowUp, ArrowDown, GitBranch, Copy, UploadSimple,
  Star, ArrowBendUpRight, DotsSixVertical, PaperPlaneTilt,
} from "@phosphor-icons/react";

export const DEPARTMENTS = [
  { value: "directie", label: "Direcție" }, { value: "vanzari", label: "Vânzări" },
  { value: "office", label: "Office" }, { value: "financiar", label: "Financiar" },
  { value: "proiectare", label: "Proiectare" }, { value: "proiectare_structuri", label: "Proiectare Structuri" },
  { value: "achizitii", label: "Achiziții" }, { value: "productie", label: "Producție" },
  { value: "gestiune", label: "Gestiune" }, { value: "logistica", label: "Logistică" },
  { value: "montaj", label: "Montaj" },
];
const ROLES = [
  { value: "admin", label: "Administrator" }, { value: "vanzari", label: "Vânzări" },
  { value: "asistent_vanzari", label: "Asistent Vânzări" }, { value: "pm", label: "Manager Proiect" },
  { value: "inginer", label: "Inginer" },
];
const SLA_UNITS = [
  { value: "business_days", label: "Zile lucrătoare" },
  { value: "calendar_days", label: "Zile calendaristice" },
  { value: "hours", label: "Ore" },
];
const ACTIONS = ["execute", "review", "approve", "sign", "clarify"];
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

function AssigneePicker({ value, users, onChange }) {
  const opts = value.kind === "user" ? users.map((u) => ({ value: u.id, label: `${u.name} · ${u.department || u.role}` }))
    : value.kind === "department" ? DEPARTMENTS : ROLES;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Select value={value.kind} onValueChange={(k) => onChange({ kind: k, ref: "", name: "" })}>
        <SelectTrigger className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="role">Rol</SelectItem>
          <SelectItem value="department">Departament</SelectItem>
          <SelectItem value="user">Persoană</SelectItem>
        </SelectContent>
      </Select>
      <Select value={value.ref || ""} onValueChange={(r) => {
        const found = opts.find((o) => o.value === r);
        onChange({ ...value, ref: r, name: found?.label || r });
      }}>
        <SelectTrigger className="h-8 w-52 text-xs"><SelectValue placeholder="Alege…" /></SelectTrigger>
        <SelectContent>{opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function NodeCard({ node, i, total, users, nodes, onChange, onMove, onRemove, dragHandlers, onUploadDoc, onDeleteDoc }) {
  const set = (k, v) => onChange({ ...node, [k]: v });
  const setSla = (k, v) => onChange({ ...node, sla: { ...node.sla, [k]: v } });
  const depthOf = (id) => { let d = 0, cur = nodes.find((x) => x.id === id), g = 0; while (cur?.parent_id && g < 20) { d++; cur = nodes.find((x) => x.id === cur.parent_id); g++; } return d; };
  const depth = depthOf(node.id);
  const parentOpts = nodes.filter((x) => x.id !== node.id);
  const addAssignee = () => set("assignees", [...(node.assignees || []), { kind: "role", ref: "", name: "" }]);
  const setAssignee = (idx, val) => set("assignees", node.assignees.map((a, j) => (j === idx ? val : a)));
  const delAssignee = (idx) => set("assignees", node.assignees.filter((_, j) => j !== idx));
  const addRoute = () => set("routes", [...(node.routes || []), { id: uid(), label: "", to: [{ kind: "department", ref: "", name: "" }], action: "execute", can_return: true, documents: [] }]);
  const setRoute = (idx, val) => set("routes", node.routes.map((r, j) => (j === idx ? val : r)));
  const delRoute = (idx) => set("routes", node.routes.filter((_, j) => j !== idx));

  return (
    <div className="rounded-lg border border-border bg-card/50 p-3" style={{ marginLeft: depth * 24 }} data-testid={`wf-node-${i}`}>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-12 md:items-end">
        <div className="flex items-center md:col-span-1" {...(dragHandlers || {})}>
          <span className="cursor-grab text-muted-foreground hover:text-foreground" data-testid={`wf-node-drag-${i}`}><DotsSixVertical size={20} weight="bold" /></span>
        </div>
        <div className="md:col-span-1">
          <label className="mb-1 block text-[10px] font-bold uppercase text-muted-foreground">Cod</label>
          <Input value={node.code || ""} onChange={(e) => set("code", e.target.value)} className="text-center" data-testid={`wf-node-code-${i}`} />
        </div>
        <div className="md:col-span-5">
          <label className="mb-1 block text-[10px] font-bold uppercase text-muted-foreground">Activitate</label>
          <Input value={node.name} onChange={(e) => set("name", e.target.value)} data-testid={`wf-node-name-${i}`} />
        </div>
        <div className="md:col-span-2">
          <label className="mb-1 block text-[10px] font-bold uppercase text-muted-foreground">Tip</label>
          <Select value={node.node_type} onValueChange={(v) => set("node_type", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="activity">Activitate</SelectItem><SelectItem value="group">Grup</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="flex gap-1 md:col-span-3 md:justify-end">
          <Button variant="ghost" size="icon" onClick={() => onMove(-1)} disabled={i === 0}><ArrowUp size={16} /></Button>
          <Button variant="ghost" size="icon" onClick={() => onMove(1)} disabled={i === total - 1}><ArrowDown size={16} /></Button>
          <Button variant="ghost" size="icon" onClick={onRemove} className="text-red-400" data-testid={`wf-node-del-${i}`}><Trash size={16} /></Button>
        </div>
      </div>

      <div className="mt-2">
        <label className="mb-1 block text-[10px] font-bold uppercase text-muted-foreground">Părinte (subcoloană) — pentru ierarhie coloană → subcoloană</label>
        <Select value={node.parent_id || "none"} onValueChange={(v) => set("parent_id", v === "none" ? null : v)}>
          <SelectTrigger className="max-w-md" data-testid={`wf-node-parent-${i}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">— fără (coloană principală) —</SelectItem>
            {parentOpts.map((p) => <SelectItem key={p.id} value={p.id}>{p.code ? `${p.code} · ` : ""}{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-3 rounded-md border border-border/60 p-2.5">
        <div className="mb-1.5 flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase text-muted-foreground">Responsabili</span>
          <div className="flex items-center gap-2">
            <Select value={node.assignment_policy} onValueChange={(v) => set("assignment_policy", v)}>
              <SelectTrigger className="h-7 w-40 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ANY_ONE">Oricine (unul)</SelectItem>
                <SelectItem value="ALL">Toți</SelectItem>
                <SelectItem value="SEQUENTIAL">Secvențial</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="h-7" onClick={addAssignee} data-testid={`wf-add-assignee-${i}`}><Plus size={13} /></Button>
          </div>
        </div>
        <div className="space-y-1.5">
          {(node.assignees || []).map((a, idx) => (
            <div key={idx} className="flex items-center gap-1.5">
              <AssigneePicker value={a} users={users} onChange={(v) => setAssignee(idx, v)} />
              <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => delAssignee(idx)}><Trash size={13} /></Button>
            </div>
          ))}
          {(node.assignees || []).length === 0 && <p className="text-xs text-muted-foreground">Niciun responsabil.</p>}
        </div>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-5">
        <div><label className="mb-1 block text-[10px] font-bold uppercase text-muted-foreground">Termen</label><Input type="number" value={node.sla?.duration ?? 3} onChange={(e) => setSla("duration", Number(e.target.value) || 0)} data-testid={`wf-node-sla-${i}`} /></div>
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase text-muted-foreground">Unitate</label>
          <Select value={node.sla?.unit || "business_days"} onValueChange={(v) => setSla("unit", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{SLA_UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><label className="mb-1 block text-[10px] font-bold uppercase text-muted-foreground">Buffer (z)</label><Input type="number" value={node.sla?.buffer_days ?? 0} onChange={(e) => setSla("buffer_days", Number(e.target.value) || 0)} /></div>
        <div><label className="mb-1 block text-[10px] font-bold uppercase text-muted-foreground">Reminder (h)</label><Input type="number" value={node.sla?.reminder_hours ?? 24} onChange={(e) => setSla("reminder_hours", Number(e.target.value) || 0)} /></div>
        <div><label className="mb-1 block text-[10px] font-bold uppercase text-muted-foreground">Țară calendar</label><Input value={node.sla?.calendar_country || "RO"} onChange={(e) => setSla("calendar_country", e.target.value.toUpperCase())} /></div>
      </div>

      <div className="mt-2">
        <label className="mb-1 block text-[10px] font-bold uppercase text-muted-foreground">Documente obligatorii (separate prin virgulă)</label>
        <Input value={(node.required_docs || []).join(", ")} onChange={(e) => set("required_docs", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} placeholder="ex: Contract semnat, Proiect tehnic aprobat" data-testid={`wf-node-docs-${i}`} />
      </div>

      <div className="mt-2 rounded-md border border-border/60 bg-white/[0.02] p-2">
        <label className="mb-1 block text-[10px] font-bold uppercase text-muted-foreground">Documente-model (atașate — trimise tuturor la „Publică și aplică")</label>
        <div className="flex flex-wrap items-center gap-1.5">
          {(node.template_docs || []).map((d) => (
            <span key={d.id} className="flex items-center gap-1 rounded border border-primary/30 bg-primary/[0.06] px-2 py-1 text-xs text-primary" title={d.filename}>
              <span className="max-w-[180px] truncate">{d.filename}</span>
              <button onClick={() => onDeleteDoc(node.id, d.id)} className="text-muted-foreground hover:text-red-400"><Trash size={11} /></button>
            </span>
          ))}
          <label className="flex cursor-pointer items-center gap-1 rounded border border-dashed border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary" data-testid={`wf-node-doc-upload-${i}`}>
            <Plus size={12} /> Atașează document-model
            <input type="file" className="hidden" onChange={(e) => { onUploadDoc(node.id, e.target.files?.[0]); e.target.value = ""; }} />
          </label>
        </div>
      </div>

      <div className="mt-2 rounded-md border border-primary/20 bg-primary/[0.04] p-2.5">
        <div className="mb-1.5 flex items-center justify-between">
          <span className="flex items-center gap-1 text-[10px] font-bold uppercase text-primary"><ArrowBendUpRight size={13} /> Rute „ping-pong"</span>
          <Button variant="outline" size="sm" className="h-7" onClick={addRoute} data-testid={`wf-add-route-${i}`}><Plus size={13} /> Rută</Button>
        </div>
        <div className="space-y-2">
          {(node.routes || []).map((r, idx) => (
            <div key={`${idx}-${r.id || "r"}`} className="rounded border border-border/60 p-2">
              <div className="flex flex-wrap items-center gap-1.5">
                <Input value={r.label} onChange={(e) => setRoute(idx, { ...r, label: e.target.value })} placeholder="Etichetă (ex. Trimite la Proiectare)" className="h-8 w-56 text-xs" />
                <AssigneePicker value={r.to?.[0] || { kind: "department", ref: "", name: "" }} users={users} onChange={(v) => setRoute(idx, { ...r, to: [v] })} />
                <Select value={r.action} onValueChange={(v) => setRoute(idx, { ...r, action: v })}>
                  <SelectTrigger className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
                <label className="flex items-center gap-1 text-xs text-muted-foreground">
                  <input type="checkbox" checked={r.can_return} onChange={(e) => setRoute(idx, { ...r, can_return: e.target.checked })} /> întoarcere
                </label>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => delRoute(idx)}><Trash size={13} /></Button>
              </div>
              <Input value={(r.documents || []).join(", ")} onChange={(e) => setRoute(idx, { ...r, documents: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} placeholder="Documente de transmis (virgulă)" className="mt-1.5 h-8 text-xs" />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
        <div><label className="mb-1 block text-[10px] font-bold uppercase text-muted-foreground">Escaladare după (h)</label><Input type="number" value={node.escalation?.after_hours ?? 48} onChange={(e) => set("escalation", { ...(node.escalation || {}), after_hours: Number(e.target.value) || 0 })} /></div>
      </div>
    </div>
  );
}

export default function Workflow() {
  const [templates, setTemplates] = useState([]);
  const [sel, setSel] = useState(null);
  const [users, setUsers] = useState([]);
  const [esc, setEsc] = useState({ remind_hours: 24, escalate_hours: 48 });
  const [drag, setDrag] = useState(null);
  const dropAt = (target) => setSel((s) => {
    if (drag === null || drag === target) return s;
    const nodes = [...s.nodes];
    const [moved] = nodes.splice(drag, 1);
    nodes.splice(target, 0, moved);
    return { ...s, nodes };
  });

  const loadTemplates = () => api.get("/wf/templates").then((r) => {
    setTemplates(r.data);
    setSel((cur) => r.data.find((t) => t.id === cur?.id) || r.data.find((t) => t.is_main) || r.data[0] || null);
  }).catch(() => {});
  useEffect(() => {
    loadTemplates();
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/escalations/config").then((r) => setEsc(r.data)).catch(() => {});
  }, []);

  const setNode = (i, val) => setSel((s) => ({ ...s, nodes: s.nodes.map((n, j) => (j === i ? val : n)) }));
  const moveNode = (i, dir) => setSel((s) => {
    const j = i + dir; if (j < 0 || j >= s.nodes.length) return s;
    const nodes = [...s.nodes]; [nodes[i], nodes[j]] = [nodes[j], nodes[i]]; return { ...s, nodes };
  });
  const removeNode = (i) => setSel((s) => ({ ...s, nodes: s.nodes.filter((_, j) => j !== i) }));
  const addNode = () => setSel((s) => ({
    ...s, nodes: [...(s.nodes || []), {
      id: uid(), parent_id: null, node_type: "activity", code: "", name: "Activitate nouă", order: (s.nodes || []).length,
      assignees: [], assignment_policy: "ANY_ONE", required_docs: [], output_docs: [], routes: [],
      sla: { duration: 3, unit: "business_days", calendar_country: "RO", buffer_days: 0, reminder_hours: 24, mode: "forward" },
      escalation: { after_hours: 48 }, required: true, run_status: "pending",
    }],
  }));

  const save = async () => {
    try {
      await api.put(`/wf/templates/${sel.id}`, {
        name: sel.name, description: sel.description,
        nodes: sel.nodes.map((n, idx) => ({ ...n, order: idx })),
      });
      toast.success("Șablon salvat");
      loadTemplates();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const createTpl = async () => {
    const { data } = await api.post("/wf/templates", { name: "Șablon nou", description: "", is_main: false });
    toast.success("Șablon creat"); await loadTemplates(); setSel(data);
  };
  const duplicate = async () => { const { data } = await api.post(`/wf/templates/${sel.id}/duplicate`); toast.success("Duplicat (Draft)"); await loadTemplates(); setSel(data); };
  const publish = async () => { try { await api.post(`/wf/templates/${sel.id}/publish`); toast.success("Publicat"); loadTemplates(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const publishApply = async () => {
    try {
      const { data } = await api.post(`/wf/templates/${sel.id}/publish-apply`);
      toast.success(`Publicat și aplicat — notificări trimise către ${data.notified} responsabili`);
      loadTemplates();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const uploadDoc = async (nodeId, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/wf/templates/${sel.id}/nodes/${nodeId}/doc`, fd);
      setSel((s) => ({ ...s, nodes: s.nodes.map((n) => (n.id === nodeId ? { ...n, template_docs: [...(n.template_docs || []), data] } : n)) }));
      toast.success("Document-model atașat");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteDoc = async (nodeId, docId) => {
    try {
      await api.delete(`/wf/templates/${sel.id}/nodes/${nodeId}/doc/${docId}`);
      setSel((s) => ({ ...s, nodes: s.nodes.map((n) => (n.id === nodeId ? { ...n, template_docs: (n.template_docs || []).filter((d) => d.id !== docId) } : n)) }));
    } catch { toast.error("Eroare la ștergere"); }
  };
  const setMain = async () => { await api.put(`/wf/templates/${sel.id}`, { is_main: true }); toast.success("Marcat ca principal"); loadTemplates(); };
  const del = async () => { try { await api.delete(`/wf/templates/${sel.id}`); toast.success("Șters"); setSel(null); loadTemplates(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const saveEsc = async () => {
    try { const { data } = await api.put("/escalations/config", { remind_hours: Number(esc.remind_hours) || 24, escalate_hours: Number(esc.escalate_hours) || 48 }); setEsc(data); toast.success("Praguri salvate"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 fade-up" data-testid="workflow-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Administrare · Direcție</p>
          <h1 className="font-head text-3xl font-extrabold tracking-tighter">Motor Workflow configurabil</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Șablon principal + versiuni. Fiecare activitate are responsabili (rol / departament / persoană),
            termen (calendar), documente și rute „ping-pong". Proiectele noi pornesc automat din șablonul principal.
          </p>
        </div>
        <Button variant="outline" onClick={createTpl} data-testid="wf-new-template-btn"><Plus size={16} className="mr-1.5" /> Șablon nou</Button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2" data-testid="wf-template-list">
        {templates.map((t) => (
          <button key={t.id} onClick={() => setSel(t)}
            className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors ${sel?.id === t.id ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/40"}`}
            data-testid={`wf-template-${t.id}`}>
            <div className="flex items-center gap-1.5 font-semibold">
              {t.is_main && <Star size={13} weight="fill" className="text-amber-400" />}
              {t.name}
            </div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">v{t.version} · {t.status === "published" ? "Publicat" : t.status === "archived" ? "Arhivat" : "Draft"} · {(t.nodes || []).length} activități</div>
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-4 rounded-xl border border-amber-500/25 bg-amber-500/[0.05] p-4" data-testid="escalation-config">
        <div><h3 className="font-head text-base font-bold">Praguri escaladare (ore)</h3><p className="text-xs text-muted-foreground">Reminder la primul prag, escaladare la Director la al doilea.</p></div>
        <div><label className="mb-1 block text-[10px] font-bold uppercase text-muted-foreground">Reminder</label><Input type="number" value={esc.remind_hours} onChange={(e) => setEsc({ ...esc, remind_hours: e.target.value })} className="w-24" data-testid="esc-remind-input" /></div>
        <div><label className="mb-1 block text-[10px] font-bold uppercase text-muted-foreground">Escaladare</label><Input type="number" value={esc.escalate_hours} onChange={(e) => setEsc({ ...esc, escalate_hours: e.target.value })} className="w-24" data-testid="esc-escalate-input" /></div>
        <Button variant="outline" onClick={saveEsc} data-testid="save-esc-config-btn"><FloppyDisk size={16} className="mr-1.5" /> Salvează</Button>
      </div>

      {!sel ? (
        <p className="py-12 text-center text-sm text-muted-foreground">Selectează sau creează un șablon.</p>
      ) : (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <GitBranch size={18} className="text-primary" />
              <Input value={sel.name} onChange={(e) => setSel({ ...sel, name: e.target.value })} className="max-w-md" data-testid="wf-name" />
            </div>
            <div className="flex flex-wrap gap-2">
              {!sel.is_main && <Button variant="outline" size="sm" onClick={setMain} data-testid="wf-setmain-btn"><Star size={15} className="mr-1" /> Principal</Button>}
              <Button variant="outline" size="sm" onClick={duplicate} data-testid="wf-duplicate-btn"><Copy size={15} className="mr-1" /> Duplică</Button>
              {sel.status !== "published" && <Button variant="outline" size="sm" onClick={publish} data-testid="wf-publish-btn"><UploadSimple size={15} className="mr-1" /> Publică</Button>}
              {sel.status !== "published" && <Button variant="outline" size="sm" onClick={publishApply} className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10" data-testid="wf-publish-apply-btn"><PaperPlaneTilt size={15} className="mr-1" /> Publică și aplică</Button>}
              <Button variant="outline" size="sm" onClick={del} className="text-red-400" data-testid="wf-delete-btn"><Trash size={15} /></Button>
              <Button size="sm" onClick={save} data-testid="save-workflow-btn"><FloppyDisk size={15} className="mr-1" /> Salvează</Button>
            </div>
          </div>
          <Textarea value={sel.description || ""} onChange={(e) => setSel({ ...sel, description: e.target.value })} placeholder="Descriere șablon…" className="mb-4 text-sm" />

          <div className="space-y-3">
            {(sel.nodes || []).map((n, i) => (
              <div key={`${i}-${n.id || "n"}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { dropAt(i); setDrag(null); }}
                className={drag === i ? "opacity-40" : ""}>
                <NodeCard node={n} i={i} total={sel.nodes.length} users={users} nodes={sel.nodes}
                  onChange={(v) => setNode(i, v)} onMove={(d) => moveNode(i, d)} onRemove={() => removeNode(i)}
                  onUploadDoc={uploadDoc} onDeleteDoc={deleteDoc}
                  dragHandlers={{ draggable: true, onDragStart: () => setDrag(i), onDragEnd: () => setDrag(null) }} />
              </div>
            ))}
            {(sel.nodes || []).length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">Nicio activitate. Apasă „Adaugă activitate".</p>}
          </div>
          <Button variant="outline" onClick={addNode} className="mt-3" data-testid="wf-add-node-btn"><Plus size={16} className="mr-1.5" /> Adaugă activitate</Button>
          {sel.status === "published" && <p className="mt-3 text-xs text-amber-400">Șablon publicat — activitățile nu se mai editează. Duplică-l pentru o versiune nouă.</p>}
        </div>
      )}
    </div>
  );
}
